import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { FaBars, FaTimes } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoError, setLogoError] = useState(null);

  // Force the image to reload with a cache buster
  const logoUrl = `/images/logo.png?v=${new Date().getTime()}`;

  useEffect(() => {
    // Debugging log to verify component mount
    console.log('Navbar mounted, attempting to load logo');

    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Explicitly test image loading in a useEffect
    const img = new Image();
    img.onload = () => {
      console.log('Logo loaded successfully!');
      setLogoLoaded(true);
    };
    img.onerror = (e) => {
      console.error('Logo failed to load:', e);
      setLogoError(`Error loading logo: ${e.type}`);
      setLogoLoaded(false);
    };
    img.src = logoUrl;
  }, [logoUrl]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Handle image error if it occurs during render
  const handleLogoError = (e) => {
    console.error('Logo error during render:', e);
    setLogoError(`Render error: ${e.type}`);
    setLogoLoaded(false);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-primary shadow-custom py-2' : 'bg-transparent py-4'}`}>
      <div className="container-custom flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="hero" smooth={true} duration={500} className="cursor-pointer">
            {logoLoaded ? (
              <img 
                src={logoUrl}
                alt="Amplified SEO" 
                className="h-18 md:h-20 object-contain" 
                style={{ maxWidth: '300px' }}
                onError={handleLogoError}
              />
            ) : (
              <>
                <h1 className="text-2xl md:text-3xl font-display text-white">
                  Amplified
                  <span className="text-accent">SEO</span>
                </h1>
                {logoError && (
                  <div className="hidden">Logo error: {logoError}</div>
                )}
              </>
            )}
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8">
          <NavLinks />
          <Link to="contact" smooth={true} duration={500} className="btn btn-accent">
            Get Started
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button onClick={toggleMenu} className="text-white text-2xl focus:outline-none">
            {isOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden bg-primary transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="container-custom py-4 flex flex-col space-y-4">
          <MobileNavLinks closeMenu={() => setIsOpen(false)} />
          <Link 
            to="contact" 
            smooth={true} 
            duration={500} 
            className="btn btn-accent w-full text-center"
            onClick={() => setIsOpen(false)}
          >
            Get Started
          </Link>
        </div>
      </div>
    </nav>
  );
};

const NavLinks = () => {
  return (
    <>
      <Link to="hero" smooth={true} duration={500} className="text-white hover:text-accent transition-colors cursor-pointer">
        Home
      </Link>
      <Link to="services" smooth={true} duration={500} className="text-white hover:text-accent transition-colors cursor-pointer">
        Services
      </Link>
      <Link to="about" smooth={true} duration={500} className="text-white hover:text-accent transition-colors cursor-pointer">
        About
      </Link>
      <Link to="testimonials" smooth={true} duration={500} className="text-white hover:text-accent transition-colors cursor-pointer">
        Testimonials
      </Link>
    </>
  );
};

const MobileNavLinks = ({ closeMenu }) => {
  return (
    <>
      <Link 
        to="hero" 
        smooth={true} 
        duration={500} 
        className="text-white hover:text-accent transition-colors cursor-pointer block py-2"
        onClick={closeMenu}
      >
        Home
      </Link>
      <Link 
        to="services" 
        smooth={true} 
        duration={500} 
        className="text-white hover:text-accent transition-colors cursor-pointer block py-2"
        onClick={closeMenu}
      >
        Services
      </Link>
      <Link 
        to="about" 
        smooth={true} 
        duration={500} 
        className="text-white hover:text-accent transition-colors cursor-pointer block py-2"
        onClick={closeMenu}
      >
        About
      </Link>
      <Link 
        to="testimonials" 
        smooth={true} 
        duration={500} 
        className="text-white hover:text-accent transition-colors cursor-pointer block py-2"
        onClick={closeMenu}
      >
        Testimonials
      </Link>
    </>
  );
};

export default Navbar;